// src/components/Navbar.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import '../assets/styles/SimulacrumImagesStyle.css';

const SimulacrumImagesNavbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const navRef = useRef(null);
    const { pathname } = useLocation();
    const inSection = (routes) => routes.some((route) =>
        pathname.toLowerCase() === route || pathname.toLowerCase().startsWith(route + '/')
    );
    const sectionClass = (routes) =>
        'nav-links nav-dropdown-trigger' + (inSection(routes) ? ' nav-section-active' : '');

    const closeDropdowns = () => {
        navRef.current?.querySelectorAll('details[open]').forEach((dropdown) => {
            dropdown.removeAttribute('open');
        });
    };

    useEffect(() => {
        const handlePointerDown = (event) => {
            const openDropdown = navRef.current?.querySelector('details[open]');

            if (openDropdown && !openDropdown.contains(event.target)) {
                closeDropdowns();
            }

            if (navRef.current && !navRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeDropdowns();
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const closeMenu = () => {
        setIsOpen(false);
        closeDropdowns();
    };

    return (
        <nav className="navbar" ref={navRef}>
            <a href="#main-content" className="skip-link">Skip to content</a>
            <div className="navbar-container">
                <Link to="/" className="navbar-logo">
                    <img src="/images/372NoBackground.png" alt="Simulacrum" className="navbar-logo-image" />
                </Link>
                <button
                    className="menu-icon"
                    onClick={toggleMenu}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls="main-navigation"
                    aria-label="Toggle navigation"
                >
                    {isOpen ? "Close" : "Menu"}
                </button>

                <ul id="main-navigation" className={isOpen ? 'nav-menu active' : 'nav-menu'}>
                    <li className="nav-item">
                        <NavLink to="/" end className="nav-links" onClick={closeMenu}>
                            Home
                        </NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink to="/services" className="nav-links" onClick={closeMenu}>
                            Services
                        </NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink to="/about" className="nav-links" onClick={closeMenu}>
                            About
                        </NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink to="/contact" className="nav-links nav-contact-link" onClick={closeMenu}>
                            Contact
                        </NavLink>
                    </li>

                    <li className="nav-item nav-item-dropdown">
                        <details className="nav-dropdown" aria-label="Creative works and collections menu">
                            <summary className={sectionClass(['/galleries', '/mimesis-castle'])}>
                                Creative Work
                            </summary>
                            <div className="nav-dropdown-menu">
                                <NavLink to="/galleries" className="nav-dropdown-link" onClick={closeMenu}>
                                    Creative Works, Collections and Collaborations
                                </NavLink>
                                <a
                                    href="https://muultispace.com/TheWeb3Connection"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    The Web3 Connection
                                </a>
                                <a
                                    href="https://suitcasetheatrearcade.com/"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    Suitcase Theatre Arcade Bookings
                                </a>
                                <a
                                    href="https://www.kylehintonprojects.com/"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    Professional Portfolio
                                </a>
                            </div>
                        </details>
                    </li>

                    <li className="nav-item nav-item-dropdown">
                        <details className="nav-dropdown" aria-label="Games menu">
                            <summary className={sectionClass(['/bunny-runner', '/god-of-thunder', '/suspicious-microwave-td', '/suspicious-microwave-terms-of-service', '/suspicious-microwave-privacy-policy'])}>Games</summary>
                            <div className="nav-dropdown-menu">
                                <NavLink to="/bunny-runner" className="nav-dropdown-link" onClick={closeMenu}>
                                    Bunny Runner
                                </NavLink>
                                <NavLink to="/god-of-thunder" className="nav-dropdown-link" onClick={closeMenu}>
                                    God of Thunder
                                </NavLink>
                                <NavLink to="/suspicious-microwave-td" className="nav-dropdown-link" onClick={closeMenu}>
                                    Suspicious Microwave TD
                                </NavLink>
                            </div>
                        </details>
                    </li>

                    <li className="nav-item nav-item-dropdown">
                        <details className="nav-dropdown" aria-label="Creative platforms and tools menu">
                            <summary className="nav-links nav-dropdown-trigger">
                                Platforms &amp; Tools
                            </summary>
                            <div className="nav-dropdown-menu">
                                <a
                                    href="https://muultispace.com/"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    MuultiSpace
                                </a>
                                <a
                                    href="https://explorer.thesimulacrum.xyz/"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    The Crumb NFT Explorer
                                </a>
                                <a
                                    href="https://audioatplay.ca/"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    Audio At Play
                                </a>
                            </div>
                        </details>
                    </li>

                    <li className="nav-item nav-item-dropdown">
                        <details className="nav-dropdown" aria-label="Web3 education menu">
                            <summary className={sectionClass(['/gm-blockchain', '/gm-web3-course', '/glossary', '/articles'])}>
                                Web3 Education
                            </summary>
                            <div className="nav-dropdown-menu">
                                <NavLink to="/gm-blockchain" className="nav-dropdown-link" onClick={closeMenu}>
                                    GM Block Chain Book
                                </NavLink>
                                <NavLink to="/gm-web3-course" className="nav-dropdown-link" onClick={closeMenu}>
                                    GM Web3 Course
                                </NavLink>
                                <NavLink to="/glossary" className="nav-dropdown-link" onClick={closeMenu}>
                                    Glossary
                                </NavLink>
                                <NavLink to="/articles" className="nav-dropdown-link" onClick={closeMenu}>
                                    Articles
                                </NavLink>
                                <a
                                    href="https://muultispace.com/TheWeb3Connection"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    The Web3 Connection
                                </a>
                                <a
                                    href="https://explorer.thesimulacrum.xyz/"
                                    className="nav-dropdown-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                >
                                    The Crumb NFT Explorer
                                </a>
                            </div>
                        </details>
                    </li>
                </ul>
            </div>
        </nav>
    );
};

export default SimulacrumImagesNavbar;
